<script>
import { VFormSDK } from "@/CommonDP/lib/vform/VFormDesigner.umd.min.js";
const { i18n, emitter, refMixin, FieldComponents, ContainerItemWrapper, containerItemMixin } = VFormSDK;
import { cloneDeep, get, intersection, isNil, isString, union, set, difference } from "lodash-es";
import { vformComponentsMap } from "../../utils";
import copySvg from "./assets/copy.svg";
import dragSvg from "./assets/drag.svg";
import addSvg from "./assets/add.svg";
import delSvg from "./assets/del.svg";
import Draggable from "vuedraggable";
const generateId = function () {
  return Math.floor(Math.random() * 100000 + Math.random() * 20000 + Math.random() * 5000);
};
export default {
  name: "CustomSubFormItem",
  componentName: "ContainerItem",
  components: {
    ContainerItemWrapper,
    ...FieldComponents
  },
  mixins: [emitter, i18n, refMixin, containerItemMixin],
  props: {
    widget: { type: Object, default: () => ({}) }
  },
  provide() {
    return {
      getSubFormFieldFlag: () => true,
      getSubFormName: () => this.tableFieldName,
      getReadMode: () => this.isReadMode
    };
  },
  inject: {
    refList: "refList",
    sfRefList: "sfRefList",
    globalModel: "globalModel",
    getReadMode: "getReadMode",
    elForm: "elForm",
    getSlaveTableList: { from: "getSlaveTableList", default: () => {} }
  },
  data() {
    return {
      checkRowIds: [],
      widgetPremissMap: {},
      rowIdData: [],
      fieldSchemaData: [], // 每行的列数据 [[第一行每列的配置],[第二行每列的配置],...]
      actionDisabled: false,
      insertDisabled: false, // 是否禁止新增、插入记录
      deleteDisabled: false, // 是否禁止删除记录
      customSubformDisabled: false
    };
  },
  computed: {
    rules() {
      const {
        widget: {
          options: { required, requiredHint, onValidate }
        }
      } = this;
      const rules = [];
      if (required) {
        rules.push({
          required: true,
          type: "array",
          trigger: ["blur"],
          message: requiredHint || "请添加数据"
        });
      }
      if (onValidate) {
        rules.push({
          validator: new Function("rule", "value", "callback", onValidate).bind(this),
          trigger: ["blur"]
        });
      }
      return rules;
    },
    tableFieldName() {
      return this.widget.options.name;
    },
    slaveTable() {
      const { getSlaveTableList, tableFieldName } = this;
      if (!getSlaveTableList) return {};
      return getSlaveTableList().find(({ slaveTableField }) => tableFieldName === slaveTableField) || {};
    },
    subFormData() {
      return this.formModel[this.tableFieldName] || [];
    },
    filterCheckedIds() {
      return intersection(this.checkRowIds, this.rowIdData);
    },
    indeterminate() {
      const { filterCheckedIds, isAllSelected } = this;
      return !isAllSelected && filterCheckedIds.length !== 0;
    },
    isAllSelected: {
      get() {
        const { rowIdData, filterCheckedIds } = this;
        return !!filterCheckedIds.length && rowIdData.length === filterCheckedIds.length;
      },
      set(value) {
        this.checkRowIds = value ? [...this.rowIdData] : [];
      }
    },
    formLabelClass() {
      const {
        widget: {
          options: { labelWrap, labelAlign }
        }
      } = this;
      return [
        "sub-form-label",
        "el-form-item__label",
        labelAlign,
        {
          "label-nowrap": !labelWrap
        }
      ];
    },
    fixedNum() {
      return this.widget.options.fixedNum || 0;
    },
    columnAttrs() {
      const {
        widget: { widgetList }
      } = this;
      const obj = {};
      for (let i = 0; i < widgetList.length; i++) {
        const {
          id,
          options: { columnWidth, required, hidden }
        } = widgetList[i];
        const style = { width: columnWidth };
        const className = {
          "table-col": true,
          "custom-col": true,
          "is-required": required,
          "hidden-col": hidden
        };

        obj[id] = { style, className };
      }
      return obj;
    },
    containerStyle() {
      const {
        widget: {
          options: { gridMaxHeight }
        }
      } = this;
      const style = {};
      gridMaxHeight && (style.maxHeight = gridMaxHeight);
      return style;
    },
    // 单行子表单plus没有disable，如果祖先组件传来只读那就是只读，如果否则如果本组件的disabled为true也是只读
    isReadMode() {
      return this.getReadMode() || this.customSubformDisabled;
    },
    // 行操作按钮位置
    leftActionColumn() {
      return (this.widget.options.actionColumnPosition || "left") === "left";
    },
    widgetDisabled() {
      return !!this.widget.options.disabled;
    },
    // 单元格遍历函数
    updateCellFn() {
      const {
        widget: {
          type,
          options: { updateCellState }
        }
      } = this;
      const fnArgNames = vformComponentsMap[type]?.updateCellState;
      return updateCellState ? new Function(...fnArgNames, updateCellState).bind(this) : undefined;
    },
    // 单元行遍历函数
    updateRowFn() {
      const {
        widget: {
          type,
          options: { updateRowState }
        }
      } = this;
      const fnArgNames = vformComponentsMap[type]?.updateRowState;
      return updateRowState ? new Function(...fnArgNames, updateRowState).bind(this) : undefined;
    },
    // 控制操作顶部逻辑
    actionHeaderState() {
      return {
        add: !(this.widgetDisabled || this.actionDisabled || this.insertDisabled),
        delete: !(this.widgetDisabled || this.actionDisabled || this.deleteDisabled)
      };
    },
    // 控制每行的操作
    rowStates() {
      const { updateRowFn, subFormData, rowIdData } = this;
      const baseActive = !(this.widgetDisabled || this.actionDisabled);
      const insert = baseActive && !this.insertDisabled;
      const delete_ = baseActive && !this.deleteDisabled;
      const defConfig = { delete: delete_, add: insert, copy: insert, sort: baseActive };
      return rowIdData.map((_id, rowIndex) => {
        // 获取对应行配置的数据
        if (!updateRowFn) return defConfig; // 默认使用通用(全允许)
        const row = subFormData[rowIndex];
        const cb = updateRowFn(row, subFormData) || {};
        const config = {};
        // 融合自定义方法的数据
        for (const key of Object.keys(defConfig)) config[key] = !!(cb[key] ?? defConfig[key]);
        return config;
      });
    },
    hasActionCol() {
      const headAction = Object.values(this.actionHeaderState).some((v) => v);
      // 可显示直接返回,减少计算量
      if (headAction) return true;
      // 判断行是否有可显示的数据
      const commands = ["add", "copy", "delete", "sort"];
      return this.rowStates.some((state = {}) => commands.some((key) => state[key]));
    }
  },
  created() {
    this.initRefList();
    this.initModelData();
    this.registerSubFormToRefList();
    this.initRowIdData(true);
    this.initFieldSchemaData();
    this.initEventHandler();
  },
  mounted() {
    this.handleSubFormFirstRowAdd(); // 默认添加首行后，主动触发相关事件！！
  },
  beforeDestroy() {
    this.unregisterFromRefList();
  },
  methods: {
    async batchDelete() {
      const { filterCheckedIds, rowIdData } = this;
      const length = filterCheckedIds.length;
      if (!length) return this.$message.warning("暂无选中数据");
      await this.$confirm(`确定批量删除${filterCheckedIds.length}条数据?`, this.i18nt("render.hint.prompt"), { type: "warning" });
      for (const id of filterCheckedIds) {
        const index = rowIdData.indexOf(id);
        if (index !== -1) await this.doDelete(index);
      }
    },
    // 拖拽完成后,仅对rowIdData进行了更新,我们还要对实际列表数据和fieldSchemaData更新
    onDragUpdate({ newIndex, oldIndex }) {
      const { fieldSchemaData, subFormData } = this;
      //  循环处理,待处理的数据列表 [[],[]]
      const replaceList = [subFormData, fieldSchemaData];
      for (const list of replaceList) {
        const a = list[newIndex];
        const b = list[oldIndex];
        this.$set(list, newIndex, b);
        this.$set(list, oldIndex, a);
      }
      this.handleSubFormRowChange(subFormData);
    },
    initModelData() {
      const {
        subFormData,
        widget: { widgetList }
      } = this;
      for (const row of subFormData) {
        // 初始化返回数据里不存在的字段
        for (const widget of widgetList) {
          const prop = widget.options.name;
          prop && this.$set(row, prop, row[prop]);
        }
      }
    },
    // 每次更新子表数据时,禁用组件样式
    async formatLayout() {
      await this.$nextTick();
      const {
        rowIdData,
        subFormData,
        updateCellFn,
        widget: { widgetList }
      } = this;
      if (!updateCellFn) return;
      let rowIndex = 0;
      const keyFn = {
        required(value) {
          this.setRequired(!!value);
        },
        readonly(value) {
          this.setReadMode(!!value);
        },
        disabled(value) {
          this.setDisabled(!!value);
        }
      };
      // 循环行
      for (const rowId of rowIdData) {
        const row = subFormData[rowIndex];
        // 循环列
        for (const {
          options: { name }
        } of widgetList) {
          // 获取到子表下组件
          const swRefName = `${name}@row${rowId}`;
          const instance = this.getWidgetRef(swRefName);
          if (instance) {
            const cellConfig = updateCellFn(name, row, subFormData, instance);
            // 获取方法返回的配置
            if (cellConfig) {
              for (const key of Object.keys(keyFn)) {
                const value = cellConfig[key];
                // 无值时不处理子组件
                if (isNil(value)) continue;
                // 有值时处理组件,设置禁用
                keyFn[key].call(instance, value);
              }
            }
          }
        }
        rowIndex++;
      }
    },
    getLabelAlign(widget, subWidget) {
      return subWidget.options.labelAlign || widget.options.labelAlign;
    },
    registerSubFormToRefList() {
      if (this.widget.type === "custom-sub-form") {
        this.sfRefList[this.tableFieldName] = this;
      }
    },
    initRowIdData(initFlag) {
      if (this.widget.type === "custom-sub-form") {
        this.rowIdData.splice(0, this.rowIdData.length); // 清除数组必须用splice，length=0不会响应式更新！！
        const subFormModel = this.subFormData;
        if (!!subFormModel && subFormModel.length > 0) {
          subFormModel.forEach(() => {
            this.rowIdData.push("id" + generateId());
          });

          if (initFlag) {
            // 注意：事件触发需延期执行，SumFormDataChange事件处理代码中可能存在尚未创建完成的组件！！
            setTimeout(() => {
              this.handleSubFormRowChange(subFormModel);
            }, 800);
          }
        }
      }
    },

    addToRowIdData() {
      this.rowIdData.push("id" + generateId());
    },

    insertToRowIdData(rowIndex) {
      this.rowIdData.splice(rowIndex, 0, "id" + generateId());
    },

    deleteFromRowIdData(rowIndex) {
      this.rowIdData.splice(rowIndex, 1);
    },

    getRowIdData() {
      return this.rowIdData;
    },

    getWidgetRefOfSubForm(widgetName, rowIndex) {
      const realWidgetName = widgetName + "@row" + this.rowIdData[rowIndex];
      return this.getWidgetRef(realWidgetName);
    },

    initFieldSchemaData() {
      const rowLength = this.rowIdData.length;
      this.fieldSchemaData.splice(0, this.fieldSchemaData.length); // 清除数组必须用splice，length=0不会响应式更新！！
      if (rowLength > 0) {
        for (let i = 0; i < rowLength; i++) {
          const fieldSchemas = [];
          this.widget.widgetList.forEach((swItem) => {
            fieldSchemas.push(this.cloneFieldSchema(swItem));
          });
          this.fieldSchemaData.push(fieldSchemas);
        }
      }
    },

    addToFieldSchemaData(rowIndex) {
      const fieldSchemas = [];
      this.widget.widgetList.forEach((swItem) => {
        fieldSchemas.push(this.cloneFieldSchema(swItem));
      });

      if (rowIndex === undefined) {
        this.fieldSchemaData.push(fieldSchemas);
      } else {
        this.fieldSchemaData.splice(rowIndex, 0, fieldSchemas);
      }
    },

    deleteFromFieldSchemaData(rowIndex) {
      this.fieldSchemaData.splice(rowIndex, 1);
    },

    cloneFieldSchema(fieldWidget) {
      const newFieldSchema = cloneDeep(fieldWidget);
      newFieldSchema.id = fieldWidget.type + generateId();
      return newFieldSchema;
    },

    initEventHandler() {
      if (this.widget.type !== "custom-sub-form") {
        return;
      }

      this.$on("setFormData", (newFormData) => {
        this.setLogDelData(get(newFormData, this.tableFieldName, []));
        this.initRowIdData(false);
        this.initFieldSchemaData();

        const subFormData = newFormData[this.tableFieldName] || [];
        setTimeout(() => {
          // 延时触发SubFormRowChange事件, 便于更新计算字段！！
          this.handleSubFormRowChange(subFormData);
        }, 800);
      });
    },

    handleSubFormFirstRowAdd() {
      if (this.widget.type !== "custom-sub-form") {
        return;
      }

      if (!!this.widget.options.showBlankRow && this.rowIdData.length === 1) {
        const oldSubFormData = this.subFormData || [];

        // 确认组件创建成功后触发事件!!
        this.$nextTick(() => {
          this.handleSubFormRowAdd(oldSubFormData, this.rowIdData[0]);
          this.handleSubFormRowChange(oldSubFormData);

          if (this.widget.options.disabled) {
            this.disableSubForm();
          }
        });
      }
    },

    addSubFormRow() {
      const newSubFormDataRow = {};
      this.widget.widgetList.forEach((subFormItem) => {
        if (subFormItem.formItemFlag) {
          newSubFormDataRow[subFormItem.options.name] = subFormItem.options.defaultValue;
        }
      });

      const oldSubFormData = this.subFormData;
      oldSubFormData.push(newSubFormDataRow);
      this.addToRowIdData();
      this.addToFieldSchemaData();

      // 确认组件创建成功后触发事件!!
      this.$nextTick(() => {
        this.handleSubFormRowAdd(oldSubFormData, this.rowIdData[oldSubFormData.length - 1]);
        this.handleSubFormRowChange(oldSubFormData);
      });
    },

    copySubFormRow(beforeFormRowIndex) {
      const oldSubFormData = this.subFormData;
      const newSubFormDataRow = {};
      this.widget.widgetList.forEach((subFormItem) => {
        if (subFormItem.formItemFlag) {
          newSubFormDataRow[subFormItem.options.name] = cloneDeep(oldSubFormData[beforeFormRowIndex][subFormItem.options.name]);
        }
      });
      beforeFormRowIndex++;
      oldSubFormData.splice(beforeFormRowIndex, 0, newSubFormDataRow);
      this.insertToRowIdData(beforeFormRowIndex);
      this.addToFieldSchemaData(beforeFormRowIndex);

      // 确认组件创建成功后触发事件!!
      this.$nextTick(() => {
        this.handleSubFormRowInsert(oldSubFormData, this.rowIdData[beforeFormRowIndex]);
        this.handleSubFormRowChange(oldSubFormData);
      });
    },

    insertSubFormRow(beforeFormRowIndex) {
      const newSubFormDataRow = {};
      this.widget.widgetList.forEach((subFormItem) => {
        if (subFormItem.formItemFlag) {
          newSubFormDataRow[subFormItem.options.name] = subFormItem.options.defaultValue;
        }
      });

      const oldSubFormData = this.subFormData;
      oldSubFormData.splice(beforeFormRowIndex, 0, newSubFormDataRow);
      this.insertToRowIdData(beforeFormRowIndex);
      this.addToFieldSchemaData(beforeFormRowIndex);

      // 确认组件创建成功后触发事件!!
      this.$nextTick(() => {
        this.handleSubFormRowInsert(oldSubFormData, this.rowIdData[beforeFormRowIndex]);
        this.handleSubFormRowChange(oldSubFormData);
      });
    },
    // 记录已删除数据
    logDeletedData(data) {
      if (!data) return;
      const {
        extraData,
        slaveTable: { pkField, slaveTableField }
      } = this;
      if (!pkField) return;
      const deleteKey = ["_DELETE_LIST_", slaveTableField];
      const lastLog = get(extraData, deleteKey) || [];
      const logs = [].concat(data).map((val) => (isString(val) ? val : val[pkField]));
      const newLog = union(lastLog, logs).filter((d) => d);
      set(extraData, deleteKey, newLog);
    },
    async doDelete(formRowIndex) {
      const oldSubFormData = this.subFormData;
      const deletedDataRow = cloneDeep(oldSubFormData[formRowIndex]);
      oldSubFormData.splice(formRowIndex, 1);
      this.deleteFromRowIdData(formRowIndex);
      this.deleteFromFieldSchemaData(formRowIndex);
      // 确认组件创建成功后触发事件!!
      await this.$nextTick();
      this.logDeletedData(deletedDataRow);
      this.handleSubFormRowDelete(oldSubFormData, deletedDataRow);
      this.handleSubFormRowChange(oldSubFormData);
    },
    async deleteSubFormRow(formRowIndex) {
      await this.$confirm(this.i18nt("render.hint.deleteSubFormRow") + "?", this.i18nt("render.hint.prompt"), {
        confirmButtonText: this.i18nt("render.hint.confirm"),
        cancelButtonText: this.i18nt("render.hint.cancel")
      });
      this.doDelete(formRowIndex);
    },

    handleSubFormRowChange(subFormData) {
      if (this.widget.options.onSubFormRowChange) {
        const customFunc = new Function("subFormData", this.widget.options.onSubFormRowChange);
        customFunc.call(this, subFormData);
      }
      this.dispatch("VFormRender", "fieldValidation", this.tableFieldName);
      this.formatLayout();
    },

    handleSubFormRowAdd(subFormData, newRowId) {
      if (this.widget.options.onSubFormRowAdd) {
        const customFunc = new Function("subFormData", "newRowId", this.widget.options.onSubFormRowAdd);
        customFunc.call(this, subFormData, newRowId);
      }
    },

    handleSubFormRowInsert(subFormData, newRowId) {
      if (this.widget.options.onSubFormRowInsert) {
        const customFunc = new Function("subFormData", "newRowId", this.widget.options.onSubFormRowInsert);
        customFunc.call(this, subFormData, newRowId);
      }
    },

    handleSubFormRowDelete(subFormData, deletedDataRow) {
      if (this.widget.options.onSubFormRowDelete) {
        const customFunc = new Function("subFormData", "deletedDataRow", this.widget.options.onSubFormRowDelete);
        customFunc.call(this, subFormData, deletedDataRow);
      }
    },

    setDisabled(flag) {
      this.widget.options.disabled = flag;
      if (flag) {
        this.disableSubForm();
      } else {
        this.enableSubForm();
      }
    },

    /**
     * 设置单行子表单是否禁止新增、插入记录
     * @param flag
     */
    setInsertDisabled(flag) {
      this.insertDisabled = flag;
    },

    /**
     * 设置单行子表单是否禁止删除记录
     * @param flag
     */
    setDeleteDisabled(flag) {
      this.deleteDisabled = flag;
    },
    setLogDelData(newList) {
      const {
        extraData,
        tableFieldName,
        formModel,
        slaveTable: { pkField, slaveTableField }
      } = this;
      if (!pkField && !newList) return;
      const oldList = get(formModel, tableFieldName, []);
      const deleteKey = ["_DELETE_LIST_", slaveTableField];
      const logIds = get(extraData, deleteKey) || [];
      const oldIds = [].concat(oldList).map((val) => (isString(val) ? val : val[pkField]));
      const newIds = [].concat(newList).map((val) => (isString(val) ? val : val[pkField]));
      const newLog = difference(
        union(logIds, oldIds).filter((d) => d),
        newIds
      );
      set(extraData, deleteKey, newLog);
    },
    /**
     * 单独给子表单赋值
     * 注意：该方法仅触发组件的onChange事件以及子表单的onFormRowChange事件，不会触发表单的onFormDataChange等其他事件！！
     * @param subFormValues
     */
    setSubFormValues(subFormValues) {
      this.setLogDelData(subFormValues);
      this.$set(this.formModel, this.tableFieldName, subFormValues);
      this.initRowIdData(false);
      this.initFieldSchemaData();

      setTimeout(() => {
        // 延时触发SubFormRowChange事件, 便于更新计算字段！！
        this.handleSubFormRowChange(subFormValues);
      }, 800);
    },

    /**
     * 单独为子表单某行的字段组件赋值
     * @param fieldName
     * @param fieldValue
     * @param rowIndex
     */
    setSubFormFieldValue(fieldName, fieldValue, rowIndex) {
      const subFormData = this.subFormData;
      subFormData[rowIndex][fieldName] = fieldValue;
      this.handleSubFormRowChange(subFormData);
    }
  },
  render() {
    const {
      widget,
      widget: { options }
    } = this;
    const FormLabel = () => {
      const { labelWidth, labelHidden } = options;
      if (labelHidden) return;
      const width = (labelWidth || 80) + "px";
      return (
        <div class={this.formLabelClass} style={{ width }}>
          {options.label}
        </div>
      );
    };
    const actionClassNames = ["table-col", "action-col"];
    const fixedName = this.fixedNum ? "fixed-col" : "";
    if (this.leftActionColumn) {
      actionClassNames.push(fixedName);
    } else {
      actionClassNames.push("last-action");
    }
    const ActionHeaderCell = () => {
      if (!this.hasActionCol) return;
      const { actionHeaderState: state } = this;
      return (
        <div class={actionClassNames}>
          操作
        {state.add ? <i class="el-icon-circle-plus-outline action-button" title="新增行" onClick={this.addSubFormRow} /> : undefined}
        {state.delete ? <i class="el-icon-delete action-button" title="批量删除" onClick={this.batchDelete} /> : undefined}
        </div>
      );
    };
    const ActionCell = ({ props: { rowIndex }}) => {
      if (!this.hasActionCol) return;
      const state = this.rowStates[rowIndex] || {};
      return (
        <div class={actionClassNames}>
          {state.sort ? <img class="icon-btn drag-handle" title="排序" src={dragSvg} /> : undefined}
          {state.copy ? <img class="icon-btn" src={copySvg} title="复制行" onClick={() => this.copySubFormRow(rowIndex)} /> : undefined}
          {state.add ? <img class="icon-btn" src={addSvg} title="添加行" onClick={() => this.insertSubFormRow(rowIndex)} /> : undefined}
          {state.delete ? <img class="icon-btn" src={delSvg} title="删除行" onClick={() => this.deleteSubFormRow(rowIndex)} /> : undefined}
        </div>
      );
    };
    const selectionClassNames = [fixedName, "table-col", "select-col"];
    const SelectionHeaderCell = () =>
      this.actionHeaderState.delete ? (
        <div class={selectionClassNames}>
          <el-checkbox vModel={this.isAllSelected} indeterminate={this.indeterminate} />
        </div>
      ) : undefined;
    const SelectionCell = ({ props: { rowId }}) =>
      this.actionHeaderState.delete ? (
        <div class={selectionClassNames}>
          <el-checkbox vModel={this.checkRowIds} label={rowId} />
        </div>
      ) : undefined;
    const indexClassNames = [fixedName, "table-col", "index-col"];

    const IndexHeaderCell = () => (options.showRowNumber ? <div class={indexClassNames}>序号</div> : undefined);
    const IndexCell = ({ props: { rowIndex }}) => (options.showRowNumber ? <div class={indexClassNames}>{rowIndex + 1}</div> : undefined);
    const TableHeader = () => {
      const {
        columnAttrs,
        widget: { widgetList }
      } = this;
      const customList = widgetList.map((subWidget) => {
        const hClass = [this.getLabelAlign(widget, subWidget), { "label-nowrap": !subWidget.options.labelWrap, "label-hidden": subWidget.options.labelHidden }];
        const labelList = [<div class="field-label">{subWidget.options.label}</div>];
        if (subWidget.options.labelIconClass) {
          let icon = <i class={subWidget.options.labelIconClass} />;
          if (subWidget.options.labelTooltip) {
            icon = <el-tooltip content={subWidget.options.labelTooltip}>{icon}</el-tooltip>;
          }
          if (subWidget.options.labelIconPosition === "rear") {
            labelList.push(icon);
          } else {
            labelList.unshift(icon);
          }
        }
        const attrs = columnAttrs[subWidget.id] || {};
        return (
          <div class={[hClass, attrs.className]} vShow={!subWidget.options.hidden} key={subWidget.id} style={attrs.style}>
            {labelList}
          </div>
        );
      });
      const cols = [...customList];
      insertAction(<ActionHeaderCell />, cols);
      cols.unshift(<SelectionHeaderCell />, <IndexHeaderCell />);
      return <div class="header-row table-row">{doFixedCol(cols, customList)}</div>;
    };
    const TableRow = ({ props: { rowId, rowIndex }}) => {
      const {
        columnAttrs,
        fieldSchemaData,
        formatWidget,
        widget: { widgetList }
      } = this;
      const customList = widgetList.map((subWidget, colIndex) => {
        const componentName = subWidget.type + "-widget";
        const attrs = columnAttrs[subWidget.id] || {};
        const CustomWidget = FieldComponents[componentName] || Vue.component(componentName);
        return (
          <div class={attrs.className} vShow={!subWidget.options.hidden} key={subWidget.id} style={attrs.style}>
            <div class="custom-content">
              <CustomWidget
                field={fieldSchemaData[rowIndex][colIndex]}
                index-of-parent-list={colIndex}
                key={subWidget.id}
                parent-list={formatWidget}
                parent-widget={widget}
                sub-form-col-index={colIndex}
                sub-form-row-id={rowId}
                sub-form-row-index={rowIndex}
              />
            </div>
          </div>
        );
      });
      const cols = [...customList];
      insertAction(<ActionCell rowIndex={rowIndex} />, cols);
      cols.unshift(<SelectionCell rowId={rowId} />, <IndexCell rowIndex={rowIndex} />);
      return (
        <div class="body-row table-row" key={rowId}>
          {doFixedCol(cols, customList)}
        </div>
      );
    };
    const FormTable = () => {
      const { containerStyle, tableFieldName, rowIdData, rules, widgetDisabled, actionDisabled } = this;
      const formClass = ["sub-form-container", { disabled: widgetDisabled || actionDisabled }];
      return (
        <div class={formClass} style={containerStyle}>
          <TableHeader />
          <Draggable list={rowIdData} handle=".drag-handle" vOn:update={this.onDragUpdate}>
            {rowIdData.map((rowId, rowIndex) => (
              <TableRow rowId={rowId} key={rowId} rowIndex={rowIndex} />
            ))}
          </Draggable>
          <ElFormItem class="base-form-item" labelWidth="0px" prop={tableFieldName} rules={rules} />
        </div>
      );
    };
    const insertAction = (node, cols) => {
      if (this.leftActionColumn) {
        cols.unshift(node);
      } else {
        cols.push(node);
      }
    };
    const doFixedCol = (cols, customList) => {
      if (!this.fixedNum) return cols;
      const lastNode = customList[this.fixedNum];
      const lastIndex = cols.indexOf(lastNode);
      const fixedList = cols.slice(0, lastIndex);
      const fixedBlock = <div class="fixed-block">{fixedList}</div>;
      const defaultList = cols.slice(lastIndex);
      return [fixedBlock, defaultList];
    };
    return (
      <container-item-wrapper class={options.customClass} vShow={!widget.options.hidden} widget={this.widget}>
        <FormLabel />
        <FormTable />
      </container-item-wrapper>
    );
  }
};
</script>
<style lang="scss" scoped src="./customSubFormItem.scss"></style>
